/**
 * platformSsotEntries.ts — de platform-bronnen in het centrale SSOT-register.
 *
 * WAAROM DEZE ENTRIES HIER STAAN EN NIET IN src/governance/
 *
 * Het centrale register (`src/governance/ssotRegistry`) catalogiseert bronnen uit
 * de contentlaag: tiers, DOK-grenzen, vraagtypes. Die laag mag niets van portalen
 * of van de database-client weten, en dat moet zo blijven — anders sleept een
 * governance-import straks de Supabase-client mee.
 *
 * Deze zes bronnen zijn platform-bronnen: rolnamen, pseudoniemen, groepen,
 * beheersingsdrempels, het break-glass-venster. Ze leven in de account- en
 * portaallaag. Daarom staan ze hier en worden ze door de SSOT Registry-pagina
 * samengevoegd met de governance-catalogus. Eén register voor de beheerder, twee
 * herkomsten in de code, geen laagdoorbreking.
 *
 * ELKE ENTRY DRAAGT ZIJN EIGEN BEWAKING
 *
 * `read()` toont wat de bron NU zegt; `guard()` legt hem naast de consument die
 * hem hoort te volgen. Die bewakingen stonden eerder los in het diagnostiek-scherm,
 * mét een eigen kopie van de naam en het bestandspad — een tweede beschrijving van
 * dezelfde bron, precies wat dit register moet voorkomen. Nu leent diagnostiek de
 * naam van de entry en toont alleen nog de uitkomst.
 */

import type { SsotEntry } from '../../../../governance';
import { ROLE_CONFIG } from '../../pages/users/types';
import { getAccountRoleLabel } from '../../../../accounts/logic/uiHelpers';
import { pseudonymFor } from '../../../shared/learnerPseudonym';
import { getMasteryBand, STRONG_THRESHOLD, SOLID_THRESHOLD, DEVELOPING_THRESHOLD } from '../../../shared/masteryBands';
import { GROUPS, GROUP_OPTIONS, formatGroupLabel } from '../../../../config/systemConfig';
import { BREAK_GLASS_MINUTES } from '../../../../data/supabase/dataAccess';
import { MASTERY_LEVEL_THRESHOLDS } from '../../../../config/constants';
import { V11_CONFIG } from '../../../../mastery/selection/selectionConfig';
import { STREAK_MILESTONES, isStreakMilestone } from '../../../../utils/streakMilestones';
import { RARITY_LABELS } from '../../../../mascot/rarityLabels';
import { getAvailableSubTests } from '../../../../assessment/utils/assessmentDomainConfig';

const ACCOUNT_ROLES = ['learner', 'teacher', 'parent', 'admin'] as const;

/** Twee vaste ids, zodat de pseudonym-bewaking niet van echte data afhangt. */
const PSEUDONYM_PROBE_A = '11111111-1111-1111-1111-111111111111';
const PSEUDONYM_PROBE_B = '22222222-2222-2222-2222-222222222222';

const PLATFORM_ENTRIES: SsotEntry[] = [{
  id: 'role-labels',
  name: 'Rolnamen',
  domain: 'platform',
  file: 'src/accounts/logic/uiHelpers.ts',
  exportName: 'getAccountRoleLabel + getAccountIcon',
  description: 'De woorden waarmee de app een account benoemt: Leerling, Ouder, Docent, Beheerder. Eén functie, geen tabel per portaal.',
  governs: 'Elke rolvermelding: sidebar, profielknop, onboarding, adminfilters en de gebruikerstabel.',
  consumers: ['admin ROLE_CONFIG', 'ProfileButton', 'onboarding', 'portal-shells'],
  notes: 'Tot 12 aug 2026 hield het adminportaal eigen labelstrings bij; `admin` heette daar "Admin" en elders "Beheerder". Eén rol met twee namen wordt door gebruikers overgenomen en is daarna niet meer terug te draaien.',
  read: () => ({
    columns: [{
      key: 'role',
      label: 'Rol'
    }, {
      key: 'label',
      label: 'Weergavenaam'
    }],
    rows: ACCOUNT_ROLES.map(role => ({
      role,
      label: getAccountRoleLabel(role)
    }))
  }),
  guard: () => {
    const mismatched = ACCOUNT_ROLES.filter(r => ROLE_CONFIG[r].label !== getAccountRoleLabel(r));
    return {
      ok: mismatched.length === 0,
      detail: mismatched.length === 0 ? 'Het adminportaal gebruikt exact dezelfde rolnamen als de zijbalk, de profielknop en de onboarding.' : `Afwijkend in dit portaal: ${mismatched.map(r => `${r} → "${ROLE_CONFIG[r].label}" i.p.v. "${getAccountRoleLabel(r)}"`).join(', ')}.`
    };
  }
}, {
  id: 'learner-pseudonym',
  name: 'Leerling-pseudoniem',
  domain: 'platform',
  file: 'src/portals/shared/learnerPseudonym.ts',
  exportName: 'pseudonymFor',
  description: 'Het label (L-XXXXX) waaronder een leerling in het adminportaal verschijnt, afgeleid uit zijn id. Deterministisch, dus hetzelfde kind houdt hetzelfde label.',
  governs: 'De accountlijst en het activiteitenlog — de twee plekken waar een beheerder leerlingen ziet zonder break glass.',
  consumers: ['adminDirectory', 'LogsPage', 'useUsersData'],
  notes: 'De RPC uit 0025 rekent met SHA-256, deze client met een rolling hash. Wat een beheerder ziet komt altijd uit deze functie; een rechtstreekse query op de database levert dus andere labels dan het scherm.',
  read: () => ({
    columns: [{
      key: 'input',
      label: 'Leerling-id (voorbeeld)'
    }, {
      key: 'label',
      label: 'Pseudoniem'
    }],
    rows: [{
      input: PSEUDONYM_PROBE_A,
      label: pseudonymFor(PSEUDONYM_PROBE_A)
    }, {
      input: PSEUDONYM_PROBE_B,
      label: pseudonymFor(PSEUDONYM_PROBE_B)
    }]
  }),
  guard: () => {
    const a = pseudonymFor(PSEUDONYM_PROBE_A);
    const again = pseudonymFor(PSEUDONYM_PROBE_A);
    const other = pseudonymFor(PSEUDONYM_PROBE_B);
    const ok = a === again && a !== other && /^L-[0-9A-Z]{1,5}$/.test(a);
    return {
      ok,
      detail: ok ? 'Stabiel en onderscheidend: twee sessies van één kind zijn als één kind te lezen, twee kinderen niet als hetzelfde.' : 'Het pseudoniem is niet meer stabiel of niet meer onderscheidend. Het activiteitenlog verliest daarmee zijn analysewaarde.'
    };
  }
}, {
  id: 'mastery-bands',
  name: 'Beheersingsbanden (volwassenschermen)',
  domain: 'platform',
  file: 'src/portals/shared/masteryBands.ts',
  exportName: 'getMasteryBand + STRONG/SOLID/DEVELOPING_THRESHOLD',
  description: 'Het gedeelde vocabulaire waarmee admin, ouder en leerkracht een beheersingsscore in woorden uitdrukken.',
  governs: 'Elke beheersingsbadge en -balk in een volwassenscherm.',
  consumers: ['admin UserDetailPanel', 'ouderportaal mastery-overzicht', 'leerkracht-dashboard'],
  notes: 'Het adminportaal had hier een eigen fork (80/60/40) naast de gedeelde drempels — twee schermen die hetzelfde kind anders beoordeelden.',
  read: () => ({
    columns: [{
      key: 'threshold',
      label: 'Vanaf'
    }, {
      key: 'label',
      label: 'Band'
    }],
    rows: [{
      threshold: `≥ ${STRONG_THRESHOLD}`,
      label: getMasteryBand(STRONG_THRESHOLD).label
    }, {
      threshold: `≥ ${SOLID_THRESHOLD}`,
      label: getMasteryBand(SOLID_THRESHOLD).label
    }, {
      threshold: `≥ ${DEVELOPING_THRESHOLD}`,
      label: getMasteryBand(DEVELOPING_THRESHOLD).label
    }, {
      threshold: `< ${DEVELOPING_THRESHOLD}`,
      label: getMasteryBand(Math.max(0, DEVELOPING_THRESHOLD - 1)).label
    }]
  }),
  guard: () => {
    const ordered = STRONG_THRESHOLD > SOLID_THRESHOLD && SOLID_THRESHOLD > DEVELOPING_THRESHOLD;
    const distinct = getMasteryBand(STRONG_THRESHOLD).label !== getMasteryBand(Math.max(0, DEVELOPING_THRESHOLD - 1)).label;
    const ok = ordered && distinct;
    return {
      ok,
      detail: ok ? `Drempels ${STRONG_THRESHOLD}/${SOLID_THRESHOLD}/${DEVELOPING_THRESHOLD} gelden voor admin, ouder en leerkracht. Het adminportaal rekent niet zelf meer.` : 'De drempels zijn niet meer oplopend of leveren geen onderscheidende labels. Twee portalen kunnen hetzelfde kind dan anders beoordelen.'
    };
  }
}, {
  id: 'pedagogical-thresholds',
  name: 'Pedagogische grenzen (motor én badge)',
  domain: 'platform',
  file: 'src/config/constants.ts',
  exportName: 'MASTERY_LEVEL_THRESHOLDS',
  description: 'De didactische grenzen tussen beginner, oefenaar en meester. Eén besluit, gebruikt door zowel de leerling-badge als de adaptieve selectiemotor.',
  governs: 'Welke band een leerling op zijn scherm ziet, én hoe de motor hem behandelt bij het kiezen van de volgende vraag.',
  consumers: ['V11_CONFIG (selectiemotor)', 'leerling-badges', 'voortgangsschermen'],
  notes: 'Deze grenzen stonden tot 12 aug 2026 twee keer, beide met het comment "update BOTH locations" — een erkenning dat er geen bron was. `V11_CONFIG` leidt ze nu af.',
  read: () => ({
    columns: [{
      key: 'band',
      label: 'Band'
    }, {
      key: 'threshold',
      label: 'Grens'
    }, {
      key: 'engine',
      label: 'Motor rekent met'
    }],
    rows: [{
      band: 'Beginner',
      threshold: MASTERY_LEVEL_THRESHOLDS.BEGINNER,
      engine: V11_CONFIG.STRUGGLING_THRESHOLD
    }, {
      band: 'Oefenaar',
      threshold: MASTERY_LEVEL_THRESHOLDS.OEFENAAR,
      engine: V11_CONFIG.LEARNING_THRESHOLD
    }, {
      band: 'Meester',
      threshold: MASTERY_LEVEL_THRESHOLDS.MEESTER,
      engine: V11_CONFIG.MASTERED_THRESHOLD
    }]
  }),
  guard: () => {
    const ok = V11_CONFIG.STRUGGLING_THRESHOLD === MASTERY_LEVEL_THRESHOLDS.BEGINNER && V11_CONFIG.LEARNING_THRESHOLD === MASTERY_LEVEL_THRESHOLDS.OEFENAAR && V11_CONFIG.MASTERED_THRESHOLD === MASTERY_LEVEL_THRESHOLDS.MEESTER;
    return {
      ok,
      detail: ok ? `${MASTERY_LEVEL_THRESHOLDS.BEGINNER}/${MASTERY_LEVEL_THRESHOLDS.OEFENAAR}/${MASTERY_LEVEL_THRESHOLDS.MEESTER} — de selectiemotor leidt deze grenzen hieruit af, dus badge en vraagkeuze plaatsen een kind altijd in dezelfde band.` : 'De adaptieve motor rekent met andere grenzen dan de weergave. Een kind kan "oefenaar" op zijn badge zien terwijl de motor hem als worstelend behandelt.'
    };
  }
}, {
  id: 'group-options',
  name: 'Groepen en hun labels',
  domain: 'platform',
  file: 'src/config/systemConfig.ts',
  exportName: 'GROUPS + GROUP_OPTIONS + formatGroupLabel',
  description: 'Welke leerjaren het product kent, en hoe ze heten. De enumeratie, de keuzelijst en het label komen uit één plek.',
  governs: 'Het bewerkformulier in admin, groepsfilters en elke "Groep N"-vermelding.',
  consumers: ['admin UserEditForm', 'useUsersData', 'leerkracht-groepsfilters'],
  notes: 'Het bewerkformulier hield een eigen lijst 6/7/8 bij. Numeriek gelijk, dus onzichtbaar — tot er een groep bijkomt en dat ene formulier stil achterblijft.',
  read: () => ({
    columns: [{
      key: 'value',
      label: 'Waarde'
    }, {
      key: 'label',
      label: 'Label'
    }],
    rows: GROUPS.map(g => ({
      value: g,
      label: formatGroupLabel(g)
    }))
  }),
  guard: () => {
    const sameLength = GROUP_OPTIONS.length === GROUPS.length;
    const ok = sameLength && GROUPS.every((g, i) => GROUP_OPTIONS[i].value === String(g) && GROUP_OPTIONS[i].label === formatGroupLabel(g));
    return {
      ok,
      detail: ok ? `${GROUPS.length} groepen; het bewerkformulier en alle tabellabels komen uit deze ene lijst. Een nieuwe groep verschijnt automatisch overal.` : 'De keuzelijst in het bewerkformulier loopt niet gelijk met de groepenlijst. Een beheerder kan een leerling dan niet in elke bestaande groep zetten.'
    };
  }
}, {
  id: 'break-glass-window',
  name: 'Break-glass-venster',
  domain: 'platform',
  file: 'src/data/supabase/dataAccess.ts',
  exportName: 'BREAK_GLASS_MINUTES',
  description: 'Hoe lang een beheerder na een gelogde aanvraag leerlinggegevens mag inzien. Het getal in de knoptekst en het getal in de aanvraag zijn hetzelfde.',
  governs: 'De duur die naar `request_data_access` gaat en die in het auditlog belandt.',
  consumers: ['BreakGlassPanel (knoptekst)', 'requestDataAccess', 'useUsersData'],
  notes: 'De RPC uit 0008 heeft zelf een default van 60 minuten. De client stuurt altijd expliciet mee, dus nu onschadelijk — maar een andere aanroeper die de parameter weglaat, krijgt stil een heel uur.',
  read: () => ({
    columns: [{
      key: 'setting',
      label: 'Instelling'
    }, {
      key: 'value',
      label: 'Waarde'
    }],
    rows: [{
      setting: 'Vensterduur (client, expliciet meegestuurd)',
      value: `${BREAK_GLASS_MINUTES} min`
    }, {
      setting: 'Server-default indien niet meegestuurd',
      value: '60 min'
    }, {
      setting: 'Servergrens',
      value: '1–480 min'
    }]
  }),
  guard: () => {
    const ok = BREAK_GLASS_MINUTES >= 1 && BREAK_GLASS_MINUTES <= 60;
    return {
      ok,
      detail: ok ? `${BREAK_GLASS_MINUTES} minuten, en de knoptekst leest datzelfde getal. Wat een beheerder ziet staan is wat er wordt aangevraagd en vastgelegd.` : `${BREAK_GLASS_MINUTES} minuten valt buiten wat het beleid ("ik zoek nu iets uit") toestaat. Een venster dat te lang openstaat is inzage zonder aanleiding.`
    };
  }
}, {
  id: 'streak-milestones',
  name: 'Streak-mijlpalen',
  domain: 'platform',
  file: 'src/utils/streakMilestones.ts',
  exportName: 'STREAK_MILESTONES + isStreakMilestone + getCrossedMilestone',
  description: 'De standen waarop een kind gevierd wordt voor een reeks goede antwoorden.',
  governs: 'Zowel de viering tijdens de sessie (StreakMilestoneBurst) als de mijlpaal in de sessieafsluiting.',
  consumers: ['StreakMilestoneBurst', 'useSessionExperience'],
  notes: 'Tot 12 aug 2026 hield de sessieafsluiting een eigen lijst bij met 50 in plaats van 60. Een kind kreeg dan bij stand 50 een feestelijke samenvatting zonder viering in de sessie, en bij 60 precies omgekeerd — beide keren lijkt de app kapot terwijl er alleen twee lijsten bestonden.',
  read: () => ({
    columns: [{
      key: 'nr',
      label: 'Mijlpaal'
    }, {
      key: 'streak',
      label: 'Bij stand'
    }],
    rows: STREAK_MILESTONES.map((m, i) => ({
      nr: i + 1,
      streak: m
    }))
  }),
  guard: () => {
    // Elke mijlpaal moet door de gedeelde toets als mijlpaal worden herkend, en
    // een stand ertussenin niet. Dat is precies wat uiteenliep toen er twee
    // lijsten waren.
    const allRecognised = STREAK_MILESTONES.every(m => isStreakMilestone(m));
    const between = STREAK_MILESTONES.map(m => m + 1).filter(n => !(STREAK_MILESTONES as readonly number[]).includes(n));
    const noFalsePositives = between.every(n => !isStreakMilestone(n));
    const ascending = STREAK_MILESTONES.every((m, i) => i === 0 || m > STREAK_MILESTONES[i - 1]);
    const ok = allRecognised && noFalsePositives && ascending;
    return {
      ok,
      detail: ok ? `${STREAK_MILESTONES.length} mijlpalen (${STREAK_MILESTONES.join(', ')}); de viering in de sessie en de sessieafsluiting lezen dezelfde lijst.` : 'De mijlpalen en de herkenning ervan lopen niet gelijk. Een kind kan dan op één plek gevierd worden en op de andere niet, bij dezelfde stand.'
    };
  }
}, {
  id: 'rarity-labels',
  name: 'Zeldzaamheidsnamen (Djay-looks)',
  domain: 'platform',
  file: 'src/mascot/rarityLabels.ts',
  exportName: 'RARITY_LABELS',
  description: 'Het woord dat een kind ziet bij elk zeldzaamheidsniveau van een Djay-look. Alleen de naam — de vormgeving hoort bij de weergave.',
  governs: 'De verzamelkast en de onthullingsanimatie bij een nieuwe look.',
  consumers: ['CollectionPage (RARITY_STYLES)', 'SkinEvolutionReveal'],
  notes: 'Niveau twee heette "Ongewoon" in de kast en "Bijzonder" in de onthulling. Een kind ziet die twee schermen binnen een minuut na elkaar en moest zelf concluderen dat het hetzelfde niveau was — terwijl vaste namen juist de reden zijn dat sparen werkt.',
  read: () => ({
    columns: [{
      key: 'level',
      label: 'Niveau'
    }, {
      key: 'label',
      label: 'Naam voor het kind'
    }],
    rows: (Object.keys(RARITY_LABELS) as Array<keyof typeof RARITY_LABELS>).map(level => ({
      level,
      label: RARITY_LABELS[level]
    }))
  }),
  guard: () => {
    const labels = Object.values(RARITY_LABELS);
    const allFilled = labels.every(l => l.trim().length > 0);
    const allDistinct = new Set(labels).size === labels.length;
    // Uitroeptekens waren de oude bron van afwijking: hetzelfde niveau kreeg
    // "Legendarisch" in de kast en "Legendarisch!" in de onthulling.
    const noPunctuation = labels.every(l => !/[!?.]/.test(l));
    const ok = allFilled && allDistinct && noPunctuation;
    return {
      ok,
      detail: ok ? `${labels.length} niveaus met een vaste naam (${labels.join(', ')}); de kast en de onthulling gebruiken dezelfde woorden.` : 'De zeldzaamheidsnamen zijn niet meer onderscheidend of dragen interpunctie. Hetzelfde niveau kan dan in twee schermen anders heten.'
    };
  }
}, {
  id: 'assessment-length',
  name: 'Toetslengte (vragen en tijdsindicatie)',
  domain: 'platform',
  file: 'src/assessment/utils/assessmentDomainConfig.ts',
  exportName: 'getSubTestDefinition + getAvailableSubTests',
  description: 'Hoeveel vragen een toets heeft en hoe lang die ongeveer duurt — per vakgebied én per groep, want een verkorte toets voor groep 6 is niet dezelfde toets als rekenen in groep 8.',
  governs: 'De toets die een leerling krijgt, de tijdsindicatie in het introscherm en de opdracht die een leerkracht klaarzet.',
  consumers: ['AssessmentIntroScreen', 'AssessmentPage', 'CreateAssessmentPanel (teacher)', 'assessmentBlueprintGenerator'],
  notes: 'Tot 13 aug 2026 stonden er twee naamgenoten naast deze bron met nul lezers: SESSION_CONFIG.CITO_QUESTION_COUNT en DEFAULT_ASSESSMENT_CONFIG.questionCount, beide 30. Wie de toets via die constanten korter maakte, veranderde niets en kreeg geen enkel signaal. Beide zijn verwijderd.',
  read: () => ({
    columns: [{
      key: 'group',
      label: 'Groep'
    }, {
      key: 'domain',
      label: 'Vakgebied'
    }, {
      key: 'questions',
      label: 'Vragen'
    }, {
      key: 'minutes',
      label: 'Minuten'
    }],
    rows: GROUPS.flatMap(g => getAvailableSubTests(g).map(sub => ({
      group: g,
      domain: sub.label,
      questions: sub.questionCount,
      minutes: sub.estimatedMinutes
    })))
  }),
  guard: () => {
    const all = GROUPS.flatMap(g => getAvailableSubTests(g));
    const positive = all.every(s => s.questionCount > 0 && s.estimatedMinutes > 0);
    // Een toets die op papier korter duurt dan één minuut per drie vragen is
    // vrijwel zeker een vergeten tijdsindicatie, niet een snelle toets.
    const plausible = all.every(s => s.estimatedMinutes >= s.questionCount / 3);
    const ok = all.length > 0 && positive && plausible;
    return {
      ok,
      detail: ok ? `${all.length} toetsvarianten over ${GROUPS.length} groepen, allemaal met een eigen aantal vragen en tijdsindicatie uit deze ene functie.` : 'Er is een toetsvariant zonder vragen of zonder geloofwaardige tijdsindicatie. Een leerling krijgt dan een lege toets of een verwachting die niet klopt.'
    };
  }
}];

/** De platform-bronnen, samengevoegd met de governance-catalogus in de admin. */
export function getPlatformSsotEntries(): SsotEntry[] {
  return PLATFORM_ENTRIES;
}
